import { defineStore } from 'pinia';
import api from '@/services/api';

export const useUploadStore = defineStore('upload', {
  state: () => ({
    uploads: [],
    currentUpload: null,
    uploadProgress: 0,
    error: null,
    isUploading: false,
  }),

  getters: {
    getUploads: (state) => state.uploads,
    getCurrentUpload: (state) => state.currentUpload,
    getUploadProgress: (state) => state.uploadProgress,
    getError: (state) => state.error,
    isUploadInProgress: (state) => state.isUploading,
  },

  actions: {
    // Upload de imagem de avatar
    async uploadAvatar(file) {
      if (!file) return;

      // Verificar tipo de arquivo
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        this.error = 'Apenas arquivos JPG e PNG são permitidos.';
        return { success: false, error: this.error };
      }

      // Verificar tamanho (3MB em bytes)
      if (file.size > 3 * 1024 * 1024) {
        this.error = 'O arquivo excede o limite de 3MB.';
        return { success: false, error: this.error };
      }

      this.isUploading = true;
      this.error = null;
      this.uploadProgress = 0;
      this.currentUpload = {
        id: Date.now(),
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: 'uploading',
      };

      try {
        const response = await api.upload.avatar(file, (progress) => {
          this.uploadProgress = progress;
          this.currentUpload.progress = progress;
        });

        if (response.data && response.data.success) {
          this.currentUpload.status = 'success';
          this.currentUpload.url = response.data.data.url;
          this.currentUpload.path = response.data.data.path;
          this.currentUpload.id = response.data.data.id;
          this.uploads.push({ ...this.currentUpload });
          return { success: true, data: response.data.data };
        } else {
          throw new Error('Falha no upload');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message || 'Erro ao fazer upload da imagem.';
        this.currentUpload.status = 'error';
        return { success: false, error: this.error };
      } finally {
        this.isUploading = false;
      }
    },

    // Upload de imagens para fornecedores
    async uploadSupplierImages(files, supplierId, imageMetadata = []) {
      if (!files || !files.length || !supplierId) return;

      const validFiles = [];
      const invalidFiles = [];

      // Verificar cada arquivo
      for (const file of files) {
        // Verificar tipo
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          invalidFiles.push({
            file,
            reason:
              'Tipo de arquivo inválido. Apenas JPG e PNG são permitidos.',
          });
          continue;
        }

        // Verificar tamanho
        if (file.size > 10 * 1024 * 1024) {
          invalidFiles.push({
            file,
            reason: 'O arquivo excede o limite de 10MB.',
          });
          continue;
        }

        // Arquivo válido
        validFiles.push(file);
      }

      // Se nenhum arquivo válido
      if (validFiles.length === 0) {
        this.error = 'Nenhum arquivo válido para upload.';
        return { success: false, error: this.error, invalidFiles };
      }

      // Verificar limite de arquivos
      if (validFiles.length > 10) {
        this.error = 'Máximo de 10 arquivos permitidos por upload.';
        return { success: false, error: this.error };
      }

      this.isUploading = true;
      this.error = null;
      this.uploadProgress = 0;

      // Criar previews para os arquivos válidos
      const uploadsWithPreview = validFiles.map((file) => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: 'uploading',
      }));

      try {
        // Preparar metadados se necessário
        let metadata = [];
        if (imageMetadata && imageMetadata.length > 0) {
          metadata = imageMetadata;
        }

        const response = await api.upload.supplierImages(
          validFiles,
          supplierId,
          metadata,
          (progress) => {
            this.uploadProgress = progress;
            // Atualizar progresso para cada upload
            uploadsWithPreview.forEach((upload) => {
              upload.progress = progress;
            });
          }
        );

        if (response.data && response.data.success) {
          // Mapear resposta da API para os uploads
          const imagesData = response.data.data.images || response.data.data;
          const result = imagesData.map((item, index) => {
            const upload =
              uploadsWithPreview[index < uploadsWithPreview.length ? index : 0];
            upload.status = 'success';
            upload.url = item.url;
            upload.id = item.id;
            upload.description = item.description;
            upload.isCover = item.isCover;
            return upload;
          });

          // Adicionar à lista de uploads
          this.uploads = [...this.uploads, ...result];
          return { success: true, data: imagesData, invalidFiles };
        } else {
          throw new Error('Falha no upload múltiplo');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message || 'Erro ao fazer upload das imagens.';
        // Marcar todos como erro
        uploadsWithPreview.forEach((upload) => {
          upload.status = 'error';
        });
        return { success: false, error: this.error, invalidFiles };
      } finally {
        this.isUploading = false;
      }
    },

    // Remover uma imagem da lista local
    removeUpload(uploadId) {
      const index = this.uploads.findIndex((upload) => upload.id === uploadId);
      if (index !== -1) {
        // Remover URL do objeto
        URL.revokeObjectURL(this.uploads[index].preview);
        this.uploads.splice(index, 1);
      }
    },

    // Remover uma imagem de fornecedor do servidor
    async deleteSupplierImage(supplierId, imageId) {
      if (!supplierId || !imageId) {
        this.error = 'ID do fornecedor e ID da imagem são obrigatórios.';
        return { success: false, error: this.error };
      }

      try {
        const response = await api.upload.deleteSupplierImage(
          supplierId,
          imageId
        );
        if (response.data && response.data.success) {
          // Remover da lista local
          this.removeUpload(imageId);
          return { success: true };
        } else {
          throw new Error('Falha ao excluir a imagem');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message || 'Erro ao excluir a imagem.';
        return { success: false, error: this.error };
      }
    },

    // Atualizar metadados de uma imagem específica de fornecedor
    async updateSupplierImageMetadata(supplierId, imageId, metadata) {
      if (!supplierId || !imageId || !metadata) {
        this.error =
          'ID do fornecedor, ID da imagem e metadados são obrigatórios.';
        return { success: false, error: this.error };
      }

      try {
        const response = await api.suppliers.updateImageMetadata(
          supplierId,
          imageId,
          metadata
        );

        if (response.data && response.data.success) {
          // Atualizar na lista local se a imagem existir
          const index = this.uploads.findIndex(
            (upload) => upload.id === imageId
          );
          if (index !== -1) {
            this.uploads[index] = {
              ...this.uploads[index],
              ...metadata,
              ...response.data.data,
            };
          }

          return { success: true, data: response.data.data };
        } else {
          throw new Error('Falha ao atualizar metadados da imagem');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          'Erro ao atualizar metadados da imagem.';
        return { success: false, error: this.error };
      }
    },

    // Adicionar novas imagens a um fornecedor existente
    async addSupplierImages(supplierId, files, metadata = [], onProgress) {
      if (!supplierId || !files || files.length === 0) {
        this.error = 'ID do fornecedor e arquivos são obrigatórios.';
        return { success: false, error: this.error };
      }

      // Validar arquivos
      const validFiles = files.filter(
        (file) =>
          ['image/jpeg', 'image/png'].includes(file.type) &&
          file.size <= 10 * 1024 * 1024
      );

      if (validFiles.length === 0) {
        this.error = 'Nenhum arquivo válido para upload.';
        return { success: false, error: this.error };
      }

      if (validFiles.length > 10) {
        this.error = 'Máximo de 10 arquivos permitidos por upload.';
        return { success: false, error: this.error };
      }

      this.isUploading = true;
      this.error = null;
      this.uploadProgress = 0;

      try {
        const formData = new FormData();

        // Adicionar arquivos
        validFiles.forEach((file) => {
          formData.append('files', file);
        });

        // Adicionar metadados se existirem
        if (metadata && metadata.length > 0) {
          formData.append('imageMetadata', JSON.stringify(metadata));
        }

        // Adicionar descrição padrão se fornecida
        if (metadata.defaultDescription) {
          formData.append('defaultDescription', metadata.defaultDescription);
        }

        const response = await api.suppliers.addImages(supplierId, formData, {
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              this.uploadProgress = percentCompleted;
              onProgress(percentCompleted);
            }
          },
        });

        if (response.data && response.data.success) {
          return { success: true, data: response.data.data };
        } else {
          throw new Error('Falha ao adicionar imagens');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message || 'Erro ao adicionar imagens.';
        return { success: false, error: this.error };
      } finally {
        this.isUploading = false;
      }
    },

    // Atualizar a ordem de múltiplas imagens de uma vez
    async updateSupplierImagesOrder(supplierId, imagesOrder) {
      if (!supplierId || !imagesOrder || !Array.isArray(imagesOrder)) {
        this.error = 'ID do fornecedor e ordem das imagens são obrigatórios.';
        return { success: false, error: this.error };
      }

      try {
        const response = await api.suppliers.updateImagesOrder(
          supplierId,
          imagesOrder
        );

        if (response.data && response.data.success) {
          return { success: true, data: response.data.data };
        } else {
          throw new Error('Falha ao atualizar ordem das imagens');
        }
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          'Erro ao atualizar ordem das imagens.';
        return { success: false, error: this.error };
      }
    },

    // Limpar todos os uploads
    clearUploads() {
      // Revogar todas as URLs de objetos para evitar vazamentos de memória
      this.uploads.forEach((upload) => {
        URL.revokeObjectURL(upload.preview);
      });
      this.uploads = [];
      this.currentUpload = null;
      this.uploadProgress = 0;
      this.error = null;
    },

    // Redefinir o estado de erro
    clearError() {
      this.error = null;
    },
  },

  // Persistir uploads entre recarregamentos de página
  persist: {
    enabled: true,
    strategies: [
      {
        key: 'uploads',
        storage: localStorage,
        paths: ['uploads'],
      },
    ],
  },
});
